import React from 'react';

/**
 * Robot pieces — retro tin-toy set.
 *
 * One stamped-tin body shared by all four robots; the FACE is what makes each
 * one a character. The face is picked from the robot's color, so the game keeps
 * full control of which colors exist — any unknown color falls back to a face
 * by hash, and every shade is derived from the hex.
 *
 * Faces: 0 visor · 1 cyclops · 2 lamps · 3 goggles
 *
 * No <defs>/gradients on purpose: `url(#id)` fragment references break under a
 * <base href> tag (CRA's index.html), which silently renders the body unfilled.
 * Shading is done with flat fills + translucent overlay shapes instead.
 */

const FACE_ORDER = [
  ['#d94f2b', '#f7931e'], // visor
  ['#2a63a8', '#29abe2'], // cyclops
  ['#e0a325', '#ed1e79'], // lamps
  ['#2f9e6e', '#22b573'], // goggles
];

const OUTLINE = '#2a1a0e';
const CAVITY = '#22160e';
const JOINT = '#3a2416';

function clamp(n) { return Math.max(0, Math.min(255, Math.round(n))); }

function shade(hex, amount) {
  const h = String(hex).replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return hex;
  const mix = amount > 0 ? 255 : 0;
  const t = Math.abs(amount);
  const to = v => clamp(v + (mix - v) * t);
  const r = to((num >> 16) & 255);
  const g = to((num >> 8) & 255);
  const b = to(num & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function faceIndex(color) {
  const c = String(color || '').toLowerCase();
  const i = FACE_ORDER.findIndex(group => group.includes(c));
  if (i !== -1) return i;
  let sum = 0;
  for (let k = 0; k < c.length; k += 1) sum += c.charCodeAt(k);
  return sum % FACE_ORDER.length;
}

function Face({ index, light }) {
  switch (index) {
    case 1: // cyclops — one big lens
      return (
        <>
          <circle cx="16" cy="13.6" r="4.4" fill={CAVITY} />
          <circle cx="16" cy="13.6" r="2.7" fill={light} />
          <circle cx="14.9" cy="12.5" r="1" fill="#fff" opacity="0.85" />
        </>
      );
    case 2: // lamps — twin bezelled headlamps + grille
      return (
        <>
          <circle cx="11.9" cy="13.8" r="3.5" fill={CAVITY} />
          <circle cx="20.1" cy="13.8" r="3.5" fill={CAVITY} />
          <circle cx="11.9" cy="13.8" r="2" fill={light} />
          <circle cx="20.1" cy="13.8" r="2" fill={light} />
          <circle cx="11.1" cy="13" r="0.75" fill="#fff" opacity="0.85" />
          <circle cx="19.3" cy="13" r="0.75" fill="#fff" opacity="0.85" />
          <rect x="13.4" y="17.1" width="5.2" height="1.2" rx="0.6" fill={OUTLINE} opacity="0.45" />
        </>
      );
    case 3: // goggles — strapped lenses
      return (
        <>
          <rect x="7.4" y="12.4" width="17.2" height="1.5" rx="0.75" fill={CAVITY} opacity="0.55" />
          <rect x="8.9" y="10.9" width="6.2" height="5.6" rx="2.4" fill={CAVITY} />
          <rect x="16.9" y="10.9" width="6.2" height="5.6" rx="2.4" fill={CAVITY} />
          <rect x="10.2" y="12.2" width="3.6" height="3" rx="1.5" fill={light} />
          <rect x="18.2" y="12.2" width="3.6" height="3" rx="1.5" fill={light} />
          <circle cx="11.2" cy="13.1" r="0.7" fill="#fff" opacity="0.85" />
          <circle cx="19.2" cy="13.1" r="0.7" fill="#fff" opacity="0.85" />
        </>
      );
    default: // visor — single lit slot
      return (
        <>
          <rect x="9.4" y="11.4" width="13.2" height="5.2" rx="2.6" fill={CAVITY} />
          <rect x="10.8" y="12.6" width="10.4" height="1.3" rx="0.65" fill={light} opacity="0.75" />
          <circle cx="20.4" cy="15" r="0.7" fill="#fff" opacity="0.5" />
        </>
      );
  }
}

function RobotIcon({ color = '#d94f2b', crop }) {
  const index = faceIndex(color);
  const light = shade(color, 0.3);
  // crop="head" — antenna + head only, for small round marks (avatars, list rows)
  const head = crop === 'head';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={head ? '6.4 0.1 19.2 19.9' : '0 0 32 32'}
      width="100%"
      height="100%"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* Antenna */}
      <rect x="15.2" y="2.6" width="1.6" height="3.6" rx="0.8" fill={JOINT} />
      <circle cx="16" cy="2.4" r="1.8" fill={color} stroke={OUTLINE} strokeWidth="1" />

      {/* Body, arms, feet — dropped for the head crop */}
      {!head && <>
      {/* Contact shadow — the piece sits on the board */}
      <ellipse cx="16" cy="29.1" rx="9" ry="1.7" fill={OUTLINE} opacity="0.22" />

      {/* Arms */}
      <rect x="6.6" y="19.6" width="2.9" height="6" rx="1.45" fill={color} stroke={OUTLINE} strokeWidth="1.3" />
      <rect x="22.5" y="19.6" width="2.9" height="6" rx="1.45" fill={color} stroke={OUTLINE} strokeWidth="1.3" />
      <rect x="7.3" y="23.1" width="1.5" height="2.5" rx="0.75" fill="#000" opacity="0.18" />
      <rect x="23.2" y="23.1" width="1.5" height="2.5" rx="0.75" fill="#000" opacity="0.18" />

      {/* Feet */}
      <rect x="10.2" y="24.6" width="4.6" height="3.6" rx="1.3" fill={JOINT} />
      <rect x="17.2" y="24.6" width="4.6" height="3.6" rx="1.3" fill={JOINT} />

      {/* Body — flat fill, then a soft bottom shade and a top sheen */}
      <rect x="9.6" y="18.4" width="12.8" height="8.4" rx="2.6" fill={color} stroke={OUTLINE} strokeWidth="1.4" />
      <path d="M10.9 24.4h10.2a1.3 1.3 0 0 1-1.3 1.75h-7.6a1.3 1.3 0 0 1-1.3-1.75z" fill="#000" opacity="0.16" />
      <rect x="11.6" y="20.4" width="8.8" height="1.5" rx="0.75" fill={OUTLINE} opacity="0.28" />
      </>}

      {/* Head — flat fill, sheen band on top, shade along the bottom edge */}
      <rect x="7.4" y="6.6" width="17.2" height="12.6" rx="4.2" fill={color} stroke={OUTLINE} strokeWidth="1.5" />
      <path d="M8.6 16.4h14.8v-1.2a3 3 0 0 1-.4 3.9H9a3 3 0 0 1-.4-2.7z" fill="#000" opacity="0.14" />
      <rect x="9.3" y="8.2" width="13.4" height="2.9" rx="1.45" fill="#fff" opacity="0.28" />
      <rect x="9.3" y="8.2" width="5.4" height="2.9" rx="1.45" fill="#fff" opacity="0.22" />

      <Face index={index} light={light} />
    </svg>
  );
}

export default RobotIcon;
